import React, { useState, useEffect } from 'react';
import {
  Box, Flex, Heading, Text, Button, Badge, Image, SimpleGrid,
  HStack, VStack, Card, CardBody, CardHeader, Tag, Wrap, WrapItem,
  useColorModeValue, useToast, Skeleton, SkeletonText,
  Tooltip, Textarea, Progress, Code, Alert, AlertIcon,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody,
  ModalFooter, ModalCloseButton, useDisclosure, Input,
  Table, Tbody, Tr, Td
} from '@chakra-ui/react';
import {
  ArrowLeft, Download, Play, Edit, Trash2, Brain, Layers,
  Clock, Database, Zap, FileText, ShieldCheck,
  BarChart3, Cpu, HardDrive, Users, Tag as TagIcon, CheckCircle,
  Package, BookOpen, ListChecks, FlaskConical, Music
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import Sidebar from '../components/Dashboard/Sidebar';
import {
  getModel, deleteModel, downloadModel, runModelDemo, reviewModel
} from '../api/modelService';
import { useAuth } from '../context/AuthContext';
import useModelThumbnail from '../hooks/useModelThumbnail';
import { getModelGuide, readableMetricName } from '../data/modelGuides';


// Category colors
const categoryColors = {
  'Classification': 'blue', 'NLP': 'purple', 'Time-Series': 'orange',
  'Audio': 'pink', 'Recommender': 'green', 'Computer Vision': 'cyan', 'Regression': 'yellow',
};

const inputTypeIcons = { 'image': '🖼️', 'audio': '🔊', 'text': '📝', 'tabular': '📊' };

const statusColors = { pending: 'yellow', approved: 'green', rejected: 'red' };


const ModelDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();

  const [model, setModel] = useState(null);
  const [loading, setLoading] = useState(true);
  const thumbnailSrc = useModelThumbnail(model);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Demo state
  const [demoFile, setDemoFile] = useState(null);
  const [demoPreview, setDemoPreview] = useState(null);
  const [demoRunning, setDemoRunning] = useState(false);
  const [demoResult, setDemoResult] = useState(null);
  const [demoError, setDemoError] = useState(null);
  const [demoSample, setDemoSample] = useState(null);  // the sample in use, to show its expected answer

  // Review state (admin)
  const [reviewNote, setReviewNote] = useState('');
  const [reviewing, setReviewing] = useState(false);

  const cardBg = useColorModeValue('white', 'gray.800');
  const pageBg = useColorModeValue('gray.50', 'gray.900');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const subtextColor = useColorModeValue('gray.600', 'gray.400');
  const codeBg = useColorModeValue('gray.50', 'gray.700');
  const demoBorderColor = useColorModeValue('teal.100', 'teal.800');
  const resultBg = useColorModeValue('green.50', 'green.900');

  useEffect(() => {
    const fetchModel = async () => {
      setLoading(true);
      try {
        const data = await getModel(id);
        setModel(data);
      } catch (err) {
        toast({ title: 'Model not found', status: 'error', duration: 3000 });
        navigate('/models');
      }
      setLoading(false);
    };
    fetchModel();
  }, [id, navigate, toast]);

  const handleDelete = async () => {
    try {
      const token = localStorage.getItem('token');
      await deleteModel(id, token);
      toast({ title: 'Model deleted', status: 'success', duration: 3000 });
      navigate('/models');
    } catch (err) {
      toast({ title: 'Delete failed', description: err.message, status: 'error' });
    }
    onDeleteClose();
  };

  const handleDownload = async () => {
    try {
      await downloadModel(model.id, model.title);
    } catch (err) {
      toast({ title: 'Download failed', description: err.response?.data?.detail || err.message, status: 'error' });
    }
  };

  const handleReview = async (decision) => {
    setReviewing(true);
    try {
      const token = localStorage.getItem('token');
      const result = await reviewModel(id, decision, reviewNote || null, token);
      setModel(result.model);
      setReviewNote('');
      toast({ title: `Model ${decision}`, status: decision === 'approved' ? 'success' : 'info', duration: 3000 });
    } catch (err) {
      toast({ title: 'Review failed', description: err.response?.data?.detail || err.message, status: 'error' });
    }
    setReviewing(false);
  };

  // Show the chosen image next to its result
  useEffect(() => {
    if (!demoFile || !demoFile.type?.startsWith('image/')) {
      setDemoPreview(null);
      return undefined;
    }
    const url = URL.createObjectURL(demoFile);
    setDemoPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [demoFile]);

  const handleDemo = async (file = demoFile) => {
    if (!file) {
      toast({ title: 'Please select an input file', status: 'warning' });
      return;
    }
    setDemoRunning(true);
    setDemoResult(null);
    setDemoError(null);

    try {
      const result = await runModelDemo(id, file);
      if (result.success) {
        setDemoResult(result.prediction);
      } else {
        setDemoError(result.error || 'Inference failed');
      }
    } catch (err) {
      const errData = err.response?.data;
      setDemoError(errData?.detail || errData?.error || err.message);
    }
    setDemoRunning(false);
  };

  // Fetch a sample file shipped with the site and run the demo on it in one click
  const handleUseSample = async (sample) => {
    try {
      const response = await fetch(`/demo-samples/${sample.file}`);
      if (!response.ok) throw new Error(`Could not load ${sample.file}`);
      const blob = await response.blob();
      const file = new File([blob], sample.file, { type: blob.type || (isImageModel ? 'image/jpeg' : 'audio/mpeg') });
      setDemoFile(file);
      setDemoSample(sample);
      await handleDemo(file);
    } catch (err) {
      toast({ title: 'Sample failed to load', description: err.message, status: 'error' });
    }
  };

  const isAdmin = user?.role === 'admin';
  const canReview = ['admin', 'expert', 'super_expert'].includes(user?.role);
  const canManage = user && model && (isAdmin || user.userId === model.uploaded_by);
  const status = model?.status || 'pending';

  const accuracy = model?.accuracy != null ? (model.accuracy * 100).toFixed(1) : null;
  const guide = getModelGuide(model);
  const catColor = model ? (categoryColors[model.category] || 'gray') : 'gray';

  // Accepted demo inputs: images for image models, audio for the rest
  const isImageModel = model?.inference_config?.task === 'image-embedding-classification';
  const demoAccept = isImageModel ? '.jpg,.jpeg,.png,.webp' : '.mp3,.wav,.flac,.ogg';
  const demoInputLabel = isImageModel
    ? 'Upload an image (JPG, PNG or WEBP, max 10 MB)'
    : 'Upload an audio file (MP3, WAV, FLAC or OGG, max 10 MB)';

  if (loading) {
    return (
      <Flex direction="row" minH="100vh" bg={pageBg}>
        <Sidebar isCollapsed={sidebarCollapsed} toggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)} />
        <Box pl={sidebarCollapsed ? "60px" : "250px"} w="full" p={8}>
          <Skeleton h="60px" mb={4} />
          <Skeleton h="200px" mb={4} />
          <SkeletonText noOfLines={8} spacing={4} />
        </Box>
      </Flex>
    );
  }

  if (!model) return null;

  // How to test, plus ready-made samples; shown to visitors and logged-in users alike
  const guideBlock = (
    <>
                  {/* Three steps to test, then ready-made samples with their expected answers */}
                  {guide && (
                    <Box>
                      <HStack mb={2}><ListChecks size={16} /><Text fontWeight="semibold" fontSize="sm">How to test this model</Text></HStack>
                      <VStack align="stretch" spacing={1} fontSize="sm" pl={1}>
                        {guide.steps.map((step, i) => (
                          <HStack key={i} align="start">
                            <Badge colorScheme="teal" borderRadius="full" minW="20px" textAlign="center">{i + 1}</Badge>
                            <Text>{step}</Text>
                          </HStack>
                        ))}
                      </VStack>
                    </Box>
                  )}

                  {guide?.samples?.length > 0 && (
                    <Box>
                      <HStack mb={2}><Music size={16} /><Text fontWeight="semibold" fontSize="sm">Try it with a sample</Text></HStack>
                      <SimpleGrid columns={{ base: 1, md: guide.samples.length }} spacing={3}>
                        {guide.samples.map((s) => (
                          <Box key={s.file} border="1px" borderColor={borderColor} borderRadius="lg" p={3}
                            bg={demoSample?.file === s.file ? resultBg : 'transparent'}>
                            <Text fontWeight="medium" fontSize="sm">{s.label}</Text>
                            <Text fontSize="xs" color={subtextColor} mb={1}>Expected: {s.expected}</Text>
                            {s.note && <Text fontSize="xs" color={subtextColor} mb={1}>{s.note}</Text>}
                            {isImageModel ? (
                              <Image src={`/demo-samples/${s.file}`} alt={s.label} maxH="90px" w="100%"
                                objectFit="contain" my={2} />
                            ) : (
                              <audio controls preload="none" src={`/demo-samples/${s.file}`}
                                style={{ width: '100%', height: '32px', margin: '6px 0' }} />
                            )}
                            <HStack spacing={2}>
                              {user ? (
                                <Button size="xs" colorScheme="teal" leftIcon={<Play size={12} />}
                                  onClick={() => handleUseSample(s)} isDisabled={demoRunning}>
                                  Use this sample
                                </Button>
                              ) : (
                                <Button size="xs" colorScheme="teal" variant="outline" leftIcon={<Play size={12} />}
                                  onClick={() => navigate('/login')}>
                                  Log in to run
                                </Button>
                              )}
                              <Button size="xs" variant="ghost" as="a" href={`/demo-samples/${s.file}`} download
                                leftIcon={<Download size={12} />}>
                                Download
                              </Button>
                            </HStack>
                          </Box>
                        ))}
                      </SimpleGrid>
                      <Text fontSize="xs" color={subtextColor} mt={2}>
                        {guide.sampleSource || 'Sample recordings from EveryAyah.com, for testing only.'} Or upload your own file below.
                      </Text>
                    </Box>
                  )}
    </>
  );

  return (
    <Flex direction="row" minH="100vh" bg={pageBg}>
      <Sidebar isCollapsed={sidebarCollapsed} toggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)} />

      <Box
        pl={sidebarCollapsed ? "60px" : "250px"}
        w="full" overflowY="auto" pb={8}
        transition="padding-left 0.2s ease"
      >
        {/* Top Navigation */}
        <Flex px={8} py={4} align="center" justify="space-between"
          borderBottom="1px" borderColor={borderColor} bg={cardBg}>
          <HStack>
            <Button variant="ghost" leftIcon={<ArrowLeft size={18} />}
              onClick={() => navigate('/models')} size="sm">
              Back to Models
            </Button>
          </HStack>
          {canManage && (
            <HStack spacing={2}>
              <Button leftIcon={<Edit size={16} />} variant="outline" size="sm"
                onClick={() => navigate(`/models/${id}/edit`)}>
                Edit
              </Button>
              <Button leftIcon={<Trash2 size={16} />} colorScheme="red" variant="ghost"
                size="sm" onClick={onDeleteOpen}>
                Delete
              </Button>
            </HStack>
          )}
        </Flex>

        <Box px={8} py={6}>
          {/* Header Section */}
          <Flex direction={{ base: 'column', lg: 'row' }} gap={6} mb={8}>
            {/* Thumbnail */}
            <Box
              w={{ base: '100%', lg: '320px' }}
              h="220px"
              borderRadius="xl"
              overflow="hidden"
              boxShadow="lg"
              flexShrink={0}
              bg={thumbnailSrc ? 'transparent' : `linear-gradient(135deg, var(--chakra-colors-${catColor}-400), var(--chakra-colors-${catColor}-600))`}
            >
              {thumbnailSrc ? (
                <Image src={thumbnailSrc} alt={model.title}
                  w="100%" h="100%" objectFit={isImageModel ? 'contain' : 'cover'} />
              ) : (
                <Flex h="100%" align="center" justify="center">
                  <Brain size={80} color="white" opacity={0.4} />
                </Flex>
              )}
            </Box>

            {/* Title & Meta */}
            <VStack align="flex-start" spacing={3} flex={1}>
              <HStack spacing={3}>
                <Badge colorScheme={catColor} px={3} py={1} borderRadius="full"
                  fontSize="sm" fontWeight="bold">
                  {model.category}
                </Badge>
                <Badge variant="outline" px={3} py={1} borderRadius="full" fontSize="sm">
                  {model.framework}
                </Badge>
                <Tooltip label={`Input: ${model.input_type}`}>
                  <Text fontSize="lg">{inputTypeIcons[model.input_type] || '📦'}</Text>
                </Tooltip>
                <Badge colorScheme={statusColors[status]} px={3} py={1} borderRadius="full"
                  fontSize="sm" textTransform="capitalize">
                  {status}
                </Badge>
              </HStack>

              <Heading size="xl">{model.title}</Heading>
              <Text color={subtextColor} fontSize="md" lineHeight="tall">
                {model.description || 'No description provided.'}
              </Text>

              <HStack spacing={4} fontSize="sm" color={subtextColor}>
                <HStack><Users size={14} /><Text>by {model.uploaded_by_name || 'Unknown'}</Text></HStack>
                {model.created_at && (
                  <HStack><Clock size={14} /><Text>{new Date(model.created_at).toLocaleDateString()}</Text></HStack>
                )}
                <HStack><Download size={14} /><Text>{model.download_count || 0} downloads</Text></HStack>
              </HStack>

              {/* Tags */}
              {model.tags && model.tags.length > 0 && (
                <Wrap spacing={2} mt={1}>
                  {model.tags.map((tag, i) => (
                    <WrapItem key={i}>
                      <Tag size="md" colorScheme="teal" variant="subtle" borderRadius="full">
                        <TagIcon size={12} style={{ marginRight: 4 }} /> {tag}
                      </Tag>
                    </WrapItem>
                  ))}
                </Wrap>
              )}

              {/* Action Buttons */}
              <Wrap spacing={3} mt={2}>
                {model.has_model_file && (
                  <WrapItem>
                    <Button leftIcon={<Download size={16} />} colorScheme="teal" size="md"
                      borderRadius="xl"
                      onClick={handleDownload}>
                      Download Model
                    </Button>
                  </WrapItem>
                )}
                {guide?.packageFile && (
                  <WrapItem>
                    <Tooltip label={guide.packageNote} hasArrow>
                      <Button as="a" href={`/model-packages/${guide.packageFile}`} download
                        leftIcon={<Package size={16} />} size="md" borderRadius="xl"
                        colorScheme="teal" variant={model.has_model_file ? 'outline' : 'solid'}>
                        {model.has_model_file ? 'Download Code Package' : 'Download Model Package'}
                      </Button>
                    </Tooltip>
                  </WrapItem>
                )}
                {!model.has_model_file && !guide?.packageFile && (
                  <WrapItem>
                    <Tooltip label="No model file uploaded yet">
                      <Button leftIcon={<Download size={16} />} isDisabled size="md"
                        borderRadius="xl">
                        Download Model
                      </Button>
                    </Tooltip>
                  </WrapItem>
                )}
              </Wrap>
              {guide?.packageNote && (
                <Text fontSize="xs" color={subtextColor} maxW="2xl">{guide.packageNote}</Text>
              )}
            </VStack>
          </Flex>

          {/* Review status */}
          {status !== 'approved' && (
            <Alert status={status === 'rejected' ? 'error' : 'warning'} borderRadius="lg" mb={6}>
              <AlertIcon />
              <VStack align="start" spacing={0}>
                <Text fontWeight="medium">
                  {status === 'rejected' ? 'This model was rejected' : 'Pending admin review'}
                </Text>
                <Text fontSize="sm">
                  {status === 'rejected'
                    ? (model.review_note || 'No reason was given.')
                    : 'Only you and the admins can see this model until it is approved.'}
                </Text>
              </VStack>
            </Alert>
          )}

          {canReview && (
            <Card bg={cardBg} borderRadius="xl" boxShadow="sm" mb={8}>
              <CardHeader pb={2}>
                <HStack><ShieldCheck size={18} /><Heading size="sm">Review</Heading></HStack>
                {model.reviewed_by_name && (
                  <Text fontSize="xs" color={subtextColor} mt={1}>
                    Last reviewed by {model.reviewed_by_name}
                    {model.reviewed_at && ` on ${new Date(model.reviewed_at).toLocaleDateString()}`}
                    {model.review_note && ` — "${model.review_note}"`}
                  </Text>
                )}
              </CardHeader>
              <CardBody pt={0}>
                <VStack align="stretch" spacing={3}>
                  {model.untrusted_types && model.untrusted_types.length > 0 && (
                    <Alert status="error" borderRadius="lg" fontSize="sm">
                      <AlertIcon />
                      The model file contains types that are not trusted by default
                      ({model.untrusted_types.join(', ')}), so the live demo is disabled.
                    </Alert>
                  )}
                  <Textarea placeholder="Review note (shown to the uploader if rejected)" rows={2}
                    value={reviewNote} onChange={e => setReviewNote(e.target.value)} />
                  <HStack>
                    <Button colorScheme="green" size="sm" isLoading={reviewing}
                      isDisabled={status === 'approved'} onClick={() => handleReview('approved')}>
                      Approve
                    </Button>
                    <Button colorScheme="red" variant="outline" size="sm" isLoading={reviewing}
                      isDisabled={status === 'rejected'} onClick={() => handleReview('rejected')}>
                      Reject
                    </Button>
                  </HStack>
                </VStack>
              </CardBody>
            </Card>
          )}

          {/* Stats Cards */}
          <SimpleGrid columns={{ base: 2, md: 4, lg: 6 }} spacing={4} mb={8}>
            {accuracy && (
              <Card bg={cardBg} borderRadius="xl" boxShadow="sm">
                <CardBody p={4} textAlign="center">
                  <Zap size={20} color="var(--chakra-colors-orange-400)" style={{ margin: '0 auto 8px' }} />
                  <Text fontSize="2xl" fontWeight="bold" color="orange.500">{accuracy}%</Text>
                  <Text fontSize="xs" color={subtextColor}>Accuracy</Text>
                </CardBody>
              </Card>
            )}
            {model.model_size_mb != null && (
              <Card bg={cardBg} borderRadius="xl" boxShadow="sm">
                <CardBody p={4} textAlign="center">
                  <HardDrive size={20} color="var(--chakra-colors-blue-400)" style={{ margin: '0 auto 8px' }} />
                  <Text fontSize="2xl" fontWeight="bold">{model.model_size_mb} MB</Text>
                  <Text fontSize="xs" color={subtextColor}>Model Size</Text>
                </CardBody>
              </Card>
            )}
            {model.training_time_sec != null && (
              <Card bg={cardBg} borderRadius="xl" boxShadow="sm">
                <CardBody p={4} textAlign="center">
                  <Clock size={20} color="var(--chakra-colors-green-400)" style={{ margin: '0 auto 8px' }} />
                  <Text fontSize="2xl" fontWeight="bold">{model.training_time_sec.toFixed(1)}s</Text>
                  <Text fontSize="xs" color={subtextColor}>Training Time</Text>
                </CardBody>
              </Card>
            )}
            {model.num_classes != null && (
              <Card bg={cardBg} borderRadius="xl" boxShadow="sm">
                <CardBody p={4} textAlign="center">
                  <Layers size={20} color="var(--chakra-colors-purple-400)" style={{ margin: '0 auto 8px' }} />
                  <Text fontSize="2xl" fontWeight="bold">{model.num_classes}</Text>
                  <Text fontSize="xs" color={subtextColor}>Classes</Text>
                </CardBody>
              </Card>
            )}
            {model.num_samples != null && (
              <Card bg={cardBg} borderRadius="xl" boxShadow="sm">
                <CardBody p={4} textAlign="center">
                  <Database size={20} color="var(--chakra-colors-cyan-400)" style={{ margin: '0 auto 8px' }} />
                  <Text fontSize="2xl" fontWeight="bold">{model.num_samples}</Text>
                  <Text fontSize="xs" color={subtextColor}>Training Samples</Text>
                </CardBody>
              </Card>
            )}
            {model.output_type && (
              <Card bg={cardBg} borderRadius="xl" boxShadow="sm">
                <CardBody p={4} textAlign="center">
                  <FileText size={20} color="var(--chakra-colors-teal-400)" style={{ margin: '0 auto 8px' }} />
                  <Text fontSize="md" fontWeight="bold" noOfLines={1}>{model.output_type}</Text>
                  <Text fontSize="xs" color={subtextColor}>Output Type</Text>
                </CardBody>
              </Card>
            )}
          </SimpleGrid>

          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} mb={8}>
            {/* Additional Metrics */}
            {model.metrics && Object.keys(model.metrics).length > 0 && (
              <Card bg={cardBg} borderRadius="xl" boxShadow="sm">
                <CardHeader pb={2}>
                  <HStack><BarChart3 size={18} /><Heading size="sm">Performance Metrics</Heading></HStack>
                </CardHeader>
                <CardBody pt={0}>
                  <Table variant="simple" size="sm">
                    <Tbody>
                      {Object.entries(model.metrics).map(([key, value]) => (
                        <Tr key={key}>
                          <Td fontWeight="medium" fontSize="sm" color={subtextColor}>
                            {readableMetricName(key)}
                          </Td>
                          <Td isNumeric fontWeight="bold">
                            {/* Scores between 0 and 1 read more easily as percentages */}
                            {typeof value === 'number'
                              ? (value > 0 && value <= 1 ? `${(value * 100).toFixed(1)}%` : value.toLocaleString())
                              : String(value)}
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </CardBody>
              </Card>
            )}

            {/* Architecture / Layers Info */}
            {model.layers_info && (
              <Card bg={cardBg} borderRadius="xl" boxShadow="sm">
                <CardHeader pb={2}>
                  <HStack><Cpu size={18} /><Heading size="sm">Model Architecture</Heading></HStack>
                </CardHeader>
                <CardBody pt={0}>
                  <Code p={4} borderRadius="md" display="block" whiteSpace="pre-wrap" fontSize="sm"
                    bg={codeBg}>
                    {model.layers_info}
                  </Code>
                </CardBody>
              </Card>
            )}
          </SimpleGrid>

          {/* What the model knows, its limits and its sources, in plain language */}
          {guide && (
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} mb={8}>
              <Card bg={cardBg} borderRadius="xl" boxShadow="sm">
                <CardHeader pb={2}>
                  <HStack><FlaskConical size={18} /><Heading size="sm">What this model knows</Heading></HStack>
                </CardHeader>
                <CardBody pt={0}>
                  <VStack align="stretch" spacing={2} fontSize="sm">
                    {guide.knows.map((line, i) => (
                      <HStack key={i} align="start"><Text color="green.500">✓</Text><Text>{line}</Text></HStack>
                    ))}
                    {guide.limits.map((line, i) => (
                      <HStack key={`l${i}`} align="start"><Text color="orange.400">!</Text><Text color={subtextColor}>{line}</Text></HStack>
                    ))}
                  </VStack>
                </CardBody>
              </Card>
              <Card bg={cardBg} borderRadius="xl" boxShadow="sm">
                <CardHeader pb={2}>
                  <HStack><BookOpen size={18} /><Heading size="sm">Sources and credits</Heading></HStack>
                </CardHeader>
                <CardBody pt={0}>
                  <VStack align="stretch" spacing={2} fontSize="sm">
                    {guide.credits.map((c) => (
                      <Text key={c.name}>
                        <a href={c.url} target="_blank" rel="noopener noreferrer"
                          style={{ color: 'var(--chakra-colors-teal-600)', fontWeight: 600 }}>{c.name}</a>
                        {' '}<Text as="span" color={subtextColor}>{c.note}</Text>
                      </Text>
                    ))}
                    <Text fontSize="xs" color={subtextColor} pt={2}>
                      To cite: {model.title}, Quranic AI Data Hub, Universiti Malaya
                      {model.created_at ? ` (${new Date(model.created_at).getFullYear()})` : ''}.
                      Available at {window.location.origin}/models/{model.id}
                    </Text>
                  </VStack>
                </CardBody>
              </Card>
            </SimpleGrid>
          )}

          {/* Demo Runner Section */}
          <Card bg={cardBg} borderRadius="xl" boxShadow="md" mb={8}
            border="2px" borderColor={demoBorderColor}>
            <CardHeader>
              <HStack>
                <Play size={20} color="var(--chakra-colors-teal-500)" />
                <Heading size="md" color="teal.600">Live Demo</Heading>
              </HStack>
              <Text fontSize="sm" color={subtextColor} mt={1}>
                Try the model with your own input data
              </Text>
            </CardHeader>
            <CardBody pt={0}>
              {!model.demo_available ? (
                <Alert status="info" borderRadius="lg">
                  <AlertIcon />
                  <VStack align="start" spacing={1}>
                    <Text fontWeight="medium">Demo not available</Text>
                    <Text fontSize="sm">
                      Live demos run scikit-learn audio and image models uploaded in the safe .skops format,
                      and the hub's built-in Quran audio models.
                      {model.has_model_file && ' This model can still be downloaded.'}
                    </Text>
                  </VStack>
                </Alert>
              ) : !user ? (
                <VStack align="stretch" spacing={4}>
                  {guideBlock}
                  <Alert status="info" borderRadius="lg">
                    <AlertIcon />
                    <Text fontSize="sm">
                      You can play and download the samples now. To run the live demo, please{' '}
                      <Button variant="link" colorScheme="teal" size="sm" onClick={() => navigate('/login')}>log in</Button>
                      {' '}or{' '}
                      <Button variant="link" colorScheme="teal" size="sm" onClick={() => navigate('/register')}>create a free account</Button>.
                    </Text>
                  </Alert>
                </VStack>
              ) : (
                <VStack align="stretch" spacing={4}>
                  {guideBlock}

                  {/* Demo instructions */}
                  {model.demo_instructions && (
                    <Alert status="info" borderRadius="lg" variant="subtle">
                      <AlertIcon />
                      <VStack align="start" spacing={0}>
                        <Text fontWeight="medium" fontSize="sm">How to use this demo</Text>
                        <Text fontSize="sm">{model.demo_instructions}</Text>
                      </VStack>
                    </Alert>
                  )}

                  {model.sample_input_description && (
                    <Alert status="warning" borderRadius="lg" variant="subtle">
                      <AlertIcon />
                      <Text fontSize="sm">
                        <strong>Expected input:</strong> {model.sample_input_description}
                      </Text>
                    </Alert>
                  )}

                  {/* File Input */}
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" mb={2}>
                      {demoInputLabel}
                    </Text>
                    <Input
                      type="file"
                      accept={demoAccept}
                      onChange={(e) => { setDemoFile(e.target.files[0]); setDemoSample(null); }}
                      p={1}
                      border="2px dashed"
                      borderColor={borderColor}
                      borderRadius="lg"
                      h="auto"
                      py={3}
                      px={4}
                    />
                    {demoFile && (
                      <Text fontSize="xs" color={subtextColor} mt={1}>
                        Selected: {demoFile.name} ({(demoFile.size / 1024).toFixed(1)} KB)
                      </Text>
                    )}
                    {demoPreview && (
                      <Image src={demoPreview} alt="Selected image" maxH="160px" mt={2}
                        objectFit="contain" borderRadius="md" border="1px" borderColor={borderColor} />
                    )}
                  </Box>

                  {/* Run Button */}
                  <Button
                    leftIcon={<Play size={16} />}
                    colorScheme="teal"
                    onClick={() => handleDemo()}
                    isLoading={demoRunning}
                    loadingText="Running inference..."
                    isDisabled={!demoFile}
                    borderRadius="xl"
                    size="lg"
                  >
                    Run Demo
                  </Button>

                  {/* Demo Progress */}
                  {demoRunning && (
                    <Progress size="sm" isIndeterminate colorScheme="teal" borderRadius="full" />
                  )}

                  {/* Demo Results */}
                  {demoResult && (
                    <Card bg={resultBg} borderRadius="lg" border="1px" borderColor="green.200">
                      <CardBody>
                        <HStack mb={3}>
                          <CheckCircle size={18} color="var(--chakra-colors-green-500)" />
                          <Heading size="sm" color="green.600">Prediction Result</Heading>
                        </HStack>

                        {demoSample && (
                          <Alert status="info" variant="left-accent" borderRadius="md" mb={3} py={2}>
                            <AlertIcon />
                            <Text fontSize="sm">
                              Sample: {demoSample.label}. <strong>Expected: {demoSample.expected}</strong>
                            </Text>
                          </Alert>
                        )}
                        
                        {demoResult.predicted_class && (
                          <Box mb={3}>
                            <Text fontSize="sm" color={subtextColor}>Predicted Class</Text>
                            <Text fontSize="xl" fontWeight="bold">{demoResult.predicted_class}</Text>
                          </Box>
                        )}

                        {demoResult.transcript && (
                          <Box mb={3}>
                            <Text fontSize="sm" color={subtextColor}>Heard (transcript)</Text>
                            <Text fontSize="xl" dir="rtl" textAlign="right" fontFamily="QuranFont, serif">
                              {demoResult.transcript}
                            </Text>
                          </Box>
                        )}

                        {demoResult.word_check && (
                          <Box mb={3}>
                            <Text fontSize="sm" color={subtextColor} mb={1}>
                              Word by word, against {demoResult.expected_ayah}
                            </Text>
                            <Flex dir="rtl" wrap="wrap" gap={2}>
                              {demoResult.word_check.map((w, i) => (
                                <Tooltip key={i} hasArrow label={
                                  w.status === 'correct' ? 'Correct'
                                    : w.status === 'missing' ? 'Not heard' : `Heard: ${w.heard}`
                                }>
                                  <Badge fontSize="lg" px={2} py={1} borderRadius="md" textTransform="none"
                                    fontFamily="QuranFont, serif"
                                    colorScheme={w.status === 'correct' ? 'green' : w.status === 'missing' ? 'gray' : 'red'}>
                                    {w.word}
                                  </Badge>
                                </Tooltip>
                              ))}
                            </Flex>
                            {demoResult.extra_words?.length > 0 && (
                              <Text fontSize="xs" color={subtextColor} mt={2}>
                                Also heard: {demoResult.extra_words.join(' ')}
                              </Text>
                            )}
                            <Text fontSize="xs" color={subtextColor} mt={2}>
                              Green: correct. Red: a different word was heard. Grey: not heard.
                            </Text>
                          </Box>
                        )}

                        {demoResult.confidence != null && (
                          <Box mb={3}>
                            <Text fontSize="sm" color={subtextColor}>
                              {demoResult.word_check ? 'Words correct' : demoResult.transcript ? 'Text match' : isImageModel ? 'Classifier score' : 'Confidence'}
                            </Text>
                            <HStack>
                              <Progress value={demoResult.confidence * 100} w="200px"
                                colorScheme="green" borderRadius="full" size="sm" />
                              <Text fontWeight="bold">{(demoResult.confidence * 100).toFixed(1)}%</Text>
                            </HStack>
                            {isImageModel && (
                              <Text fontSize="xs" color={subtextColor} mt={1}>
                                This score is uncalibrated. It is not the probability that the judgment is correct.
                              </Text>
                            )}
                          </Box>
                        )}

                        {demoResult.top_k && demoResult.top_k.length > 0 && (
                          <Box>
                            <Text fontSize="sm" color={subtextColor} mb={2}>Top Predictions</Text>
                            {demoResult.top_k[0].same_text_as?.length > 0 && (
                              <Text fontSize="xs" color={subtextColor} mb={2}>
                                The same words also appear at {demoResult.top_k[0].same_text_as.join(', ')}.
                              </Text>
                            )}
                            <VStack align="stretch" spacing={2}>
                              {demoResult.top_k
                                .map(({ label: cls, score: prob }) => (
                                  <HStack key={cls} fontSize="sm">
                                    <Text w="200px" fontWeight="medium" noOfLines={1}>{cls}</Text>
                                    <Progress value={prob * 100} flex={1}
                                      colorScheme={prob > 0.5 ? 'green' : prob > 0.2 ? 'yellow' : 'gray'}
                                      borderRadius="full" size="sm" />
                                    <Text w="60px" textAlign="right" fontWeight="bold">
                                      {(prob * 100).toFixed(1)}%
                                    </Text>
                                  </HStack>
                                ))}
                            </VStack>
                          </Box>
                        )}

                        {guide?.resultHelp && (
                          <Text fontSize="xs" color={subtextColor} mt={3}>
                            How to read this: {guide.resultHelp}
                          </Text>
                        )}
                      </CardBody>
                    </Card>
                  )}

                  {/* Demo Error */}
                  {demoError && (
                    <Alert status="error" borderRadius="lg">
                      <AlertIcon />
                      <VStack align="start" spacing={1}>
                        <Text fontWeight="medium">Inference Error</Text>
                        <Text fontSize="sm">{demoError}</Text>
                        {model.demo_instructions && (
                          <Text fontSize="xs" color={subtextColor}>
                            Tip: {model.demo_instructions}
                          </Text>
                        )}
                      </VStack>
                    </Alert>
                  )}

                </VStack>
              )}
            </CardBody>
          </Card>
        </Box>

        {/* Delete Confirmation */}
        <Modal isOpen={isDeleteOpen} onClose={onDeleteClose} isCentered>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Delete Model</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              Are you sure you want to delete <strong>{model.title}</strong>? 
              This will permanently remove the model file and all metadata.
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onDeleteClose}>Cancel</Button>
              <Button colorScheme="red" onClick={handleDelete}>Delete</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Box>
    </Flex>
  );
};

export default ModelDetail;
